import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import {
  DeleteMessageRequest,
  ENVIRONMENT,
  FetchMessagesRequest,
  MarkMessageReadRequest,
  SecureMessageResponse,
  SendMessageRequest,
  UnreadCountRequest,
} from '@ld.consumer-portal/models';

@Injectable({ providedIn: 'root' })
export class SecureMessageService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);

  private get inboxBaseUrl(): string {
    const melloServUrl = this.env.appSettings.melloServDomainUrl || '';
    const apiNamespace = this.env.appSettings.apiNamespace || 'api/v1';
    return `${melloServUrl}/${apiNamespace}/MelloServDomain/SecureMessageRequest/Inbox`;
  }

  private get crmBaseUrl(): string {
    return this.env.appSettings.MelloServCRMUrl || '';
  }

  async getMessages(request: FetchMessagesRequest): Promise<SecureMessageResponse> {
    return firstValueFrom(this.http.post<SecureMessageResponse>(`${this.inboxBaseUrl}/Messages`, request));
  }

  async deleteMessage(request: DeleteMessageRequest): Promise<void> {
    return firstValueFrom(this.http.post<void>(`${this.inboxBaseUrl}/Messages/Delete`, request));
  }

  async markAsRead(request: MarkMessageReadRequest): Promise<void> {
    return firstValueFrom(this.http.post<void>(`${this.inboxBaseUrl}/Messages/MarkRead`, request));
  }

  async getUnreadCount(request: UnreadCountRequest): Promise<{ count: number }> {
    return firstValueFrom(this.http.post<{ count: number }>(`${this.inboxBaseUrl}/Messages/UnreadCount`, request));
  }

  async sendMessage(request: SendMessageRequest, attachments?: File[]): Promise<void> {
    const formData = new FormData();

    formData.append('title', request.title || '');
    formData.append('message', request.message || '');
    formData.append('firstName', request.firstName || '');
    formData.append('lastName', request.lastName || '');
    formData.append('phoneNumber', request.phoneNumber || '');
    formData.append('emailAddress', request.emailAddress || '');
    formData.append('faxNumber', request.faxNumber || '');
    formData.append('loanNumber', request.loanNumber || '');
    formData.append('zipCode', request.zipCode || '');
    formData.append('caseId', request.caseId || '');
    formData.append('correlationId', request.correlationId || '');
    formData.append('requestCategory', request.requestCategory || '');
    formData.append('requestSubCategory', request.requestSubCategory || '');
    formData.append('requestTimeStamp', request.requestTimeStamp || '');
    formData.append('encryptionValue', request.encryptionValue || '');

    if (attachments?.length) {
      attachments.forEach((file) => {
        formData.append('documentList', file, file.name);
      });
    }

    return firstValueFrom(this.http.post<void>(`${this.crmBaseUrl}/SecureMessagingRequest/ServicingPortal`, formData));
  }

  async downloadAttachment(request: {
    LoanNumber: string;
    EncryptionValue: string;
    DocumentPath: string;
  }): Promise<{ DownloadUrl: string }> {
    return firstValueFrom(this.http.post<{ DownloadUrl: string }>(`${this.crmBaseUrl}/Document/Download`, request));
  }
}
